/**
 * Input mapper — the ONE place that translates user intent into typed
 * `ClientMessage` values. Pure: no DOM APIs, no side effects. The
 * `InputController` wires the mapper to DOM events; unit tests drive
 * the mapper with synthetic events directly.
 *
 * See wp7-client-ui-input.md § "Input mapping".
 *
 * Design commitments:
 *   • Every branch either returns a `ClientMessage` OR `null` (no
 *     message).
 *   • Messages are built with `@pit/protocol` typed constructors — the
 *     zod schemas are the source of truth for shape.
 *   • Set-target / clear-target ARE allowed pre-combat (spectate UX).
 *     Same choice matches the wp7 doc's "canvas focus" language: we
 *     only stop when there's no snapshot / not in the match.
 *   • `seq` is NOT a mapper concern — the socket wrapper assigns it.
 *     The mapper returns the payload half (`{ input, kind: ... }`);
 *     the controller wraps it in `{ type: "input", seq, input }`.
 */

import {
  toEntityId as _toEntityId,
  toUnits as _toUnits,
  type AbilitySlot,
  type EntityId,
  type SimInput,
  type TargetType,
  type WireSimState,
  type Units,
} from "@pit/protocol";
import { screenToWorld } from "../render/projection.js";
import type { ProjectionCenter } from "../render/projection.js";
import { hitTestEntity } from "./hitTest.js";
import {
  DEFAULT_KEYMAP,
  buildReverseIndex,
  normalizeChord,
  chordId,
  type GameAction,
  type Keymap,
} from "./keybinding/index.js";
import {
  nextEnemyTargetWire,
  nearestEnemyTargetWire,
  hasLiveEnemyTargetWire,
  nextAllyTargetWire,
  enemyByIndexWire,
  allyByIndexWire,
} from "./targetSelectWire.js";

/** Canvas-relative pointer position. See wp7 traps: canvas coords, not
 *  clientX/Y — the caller converts via `getBoundingClientRect`. */
export interface CanvasPoint {
  readonly x: number;
  readonly y: number;
}

/** Minimal shape of a mouse event we care about. Structural so tests
 *  don't need real DOM `MouseEvent` instances. */
export interface CanvasMouseEvent {
  readonly button: number; // 0=left, 2=right (DOM convention)
  readonly point: CanvasPoint;
}

/** Minimal shape of a keyboard event we care about. */
export interface KeyEvent {
  /** `KeyboardEvent.key` — e.g. "1", "Tab", "Escape", "s". */
  readonly key: string;
  /** Whether this is a synthetic auto-repeat (holding key). */
  readonly repeat: boolean;
  /** Modifier flags. Optional — typed for #68 but ignored by v1 matching. */
  readonly ctrl?: boolean;
  readonly alt?: boolean;
  readonly shift?: boolean;
  readonly meta?: boolean;
}

/**
 * Context the mapper reads from — pure snapshot data plus WP6 render
 * offset (canvas-relative screen→world needs it).
 */
export interface MapperContext {
  /** Latest wire snapshot (may be `null` before first arrival). */
  readonly snapshot: WireSimState | null;
  /**
   * Own seat. Determines whose entity we're piloting. `null` before the
   * server's `match.joined` tells us our real seat (see `store/reducer.ts`) —
   * NEVER defaulted to `0`: a wrong seat here silently inverts enemy/ally
   * target resolution (#731). The four seat-relative cases below (
   * `cycleEnemyTarget`/`cycleAllyTarget`/`arenaTarget`/`partyTarget`) treat a
   * `null` seat as "unknown" and no-op rather than resolve against seat 0.
   */
  readonly ownSeat: number | null;
  /** Screen offset from WP6's arena centering (canvas-relative). */
  readonly projectionCenter: ProjectionCenter;
  /**
   * Effective keymap (already resolved account→browser→defaults). The mapper
   * builds a reverse index from it per call; callers that care about hot-path
   * cost can memoize the index, but correctness does not depend on it.
   */
  readonly keymap: Keymap;
  /**
   * Last known cursor position in CANVAS coords (#36). Keyboard cast events
   * carry no pointer position, so the surface tracks the latest pointer move
   * and passes it here; the mapper projects it to a world aim point for
   * skillshot casts. `null` if the cursor hasn't moved over the surface yet.
   */
  readonly lastPointer: CanvasPoint | null;
  /**
   * #417/#530: resolves a VISUAL action-bar slot — the position a keybind is
   * physically bound to ("castSlot 0" means "the leftmost bar position",
   * not a fixed ability) — to the underlying CANONICAL ability slot
   * currently displayed there per the player's saved per-champion
   * drag-and-drop layout AND current loadout selection (the single shared
   * resolver in `champions/abilityLayout.ts`). Optional so every existing
   * call site/test that builds a `MapperContext` without a layout keeps
   * identity behavior (visual === canonical, matching every layout-less
   * champion today — this remap is purely additive/opt-in). `null` means the
   * visual position is EMPTY (no ability there — fewer selected abilities
   * than bar positions) — `actionToIntent` no-ops rather than guess a
   * fallback canonical slot. `InputSurface.buildContext` supplies the real
   * resolver; `ActionBar`'s click-to-cast path resolves the canonical slot
   * itself at render time and doesn't need this (it builds the `castSlot`
   * action with the canonical slot directly).
   */
  readonly resolveActionSlot?: (visualSlot: AbilitySlot) => AbilitySlot | null;
  /**
   * #815: resolves a CANONICAL ability slot (already remapped by
   * `resolveActionSlot`) to that ability's authored `targetType`, or `null`
   * when the slot is empty / the target type is unknown. Supplied by the two
   * cast entry points (`InputSurface.buildContext`, `ActionBar`'s
   * click-to-cast) from the content-derived `abilityTargetType` table.
   *
   * The ONLY thing auto-acquire branches on: an `enemy`-targetType cast with
   * no valid hostile target acquires the nearest enemy first. Optional so a
   * `MapperContext` built without it (every pre-#815 call site/test) keeps
   * byte-identical behavior — no resolver ⇒ no auto-acquire, ever.
   */
  readonly resolveTargetType?: (canonicalSlot: AbilitySlot) => TargetType | null;
}

/** A `SimInput` payload — `cast`, `moveDir`, `setTarget`, etc. */
export type IntentPayload = SimInput;

/**
 * Result of a mapping — nothing, one intent, or (since #815) an ORDERED pair
 * of intents from a single press.
 *
 * `intents` exists solely for auto-acquire: an `enemy`-targetType cast with no
 * valid target resolves to `[setTarget, cast]`, and the ORDER is load-bearing
 * — the sim must see the target land before the cast that reads it. Callers
 * must send every element, in order; a caller that only handles `intent`
 * would silently drop the acquisition. Single-intent results keep the
 * `intent` shape so every pre-#815 branch is untouched.
 */
export type MapResult =
  | { readonly kind: "none" }
  | { readonly kind: "intent"; readonly input: IntentPayload }
  | { readonly kind: "intents"; readonly inputs: readonly IntentPayload[] };

/**
 * Every intent a `MapResult` carries, in send order (empty for `none`). The
 * ONE place callers should flatten a result — `InputSurface` and `ActionBar`
 * both use it so neither can forget the #815 two-intent case.
 */
export function mapResultInputs(result: MapResult): readonly IntentPayload[] {
  switch (result.kind) {
    case "none":
      return [];
    case "intent":
      return [result.input];
    case "intents":
      return result.inputs;
  }
}

/** Convenience — "no message". */
export const NO_MESSAGE: MapResult = { kind: "none" };

/**
 * #565: the ONE canvas-point → branded world-`Units` conversion — every
 * caller that needs to turn a canvas coordinate into a wire-legal world
 * point routes through here (`castAimFromPointer` below, and
 * `cursorTracker.ts`'s per-tick `aimPoint` wire stream), so the clamp +
 * brand logic only lives once. Negatives clamp to 0 so the server's `Units`
 * brand (must be non-negative) parses; overshoot on the positive side is
 * truncated to a large-but-legal value in the `toUnits` constructor if
 * needed. Returns `null` only if `toUnits` itself throws (defensive — in
 * practice callers rarely trigger this, since points land inside the
 * map rectangle). The `screenToWorld` PROJECTION math itself (`projection.ts`) is
 * unchanged/canonical — this wraps it, it does not replace it.
 */
export function canvasPointToWorldUnits(
  point: CanvasPoint,
  center: ProjectionCenter,
): { readonly x: Units; readonly y: Units } | null {
  const w = screenToWorld(point.x, point.y, center);
  const wx = Math.max(0, w.x);
  const wy = Math.max(0, w.y);
  try {
    return { x: _toUnits(wx), y: _toUnits(wy) };
  } catch {
    return null;
  }
}

/** The four WASD-relative directional flags (#252). */
export type WasdDirection = "forward" | "backward" | "strafeLeft" | "strafeRight";

/** Live held-state for the four WASD keys — one boolean per direction. */
export type WasdHeldState = Readonly<Record<WasdDirection, boolean>>;

/** All-released held state — the natural "no keys down" starting point. */
export const NO_WASD_HELD: WasdHeldState = {
  forward: false,
  backward: false,
  strafeLeft: false,
  strafeRight: false,
};

/**
 * Hardcoded WASD → direction mapping (case-insensitive `KeyboardEvent.key`).
 * Deliberately NOT part of the rebindable `GameAction`/`Keymap` system (#70) —
 * movement keys are a fixed physical layout, like arrow keys, not an ability
 * hotkey. Returns `null` for every other key.
 */
export function wasdDirectionForKey(key: string): WasdDirection | null {
  switch (key.toLowerCase()) {
    case "w":
      return "forward";
    case "a":
      return "strafeLeft";
    case "s":
      return "backward";
    case "d":
      return "strafeRight";
    default:
      return null;
  }
}

/**
 * Build a `moveDir` `SimInput` from the current held state (#252). Pure.
 * #351: stamps the CURRENT `wasdStrafeMode` preference onto the message —
 * the sim resolves the strafe axis, this is purely "which preference is
 * active right now" (no client-side directional remap). Defaults to
 * `"facing"` (byte-identical to pre-#351) when the caller omits it.
 */
export function buildMoveDirInput(
  held: WasdHeldState,
  strafeMode: "facing" | "screen" = "facing",
): SimInput {
  return {
    kind: "moveDir",
    forward: held.forward,
    backward: held.backward,
    strafeLeft: held.strafeLeft,
    strafeRight: held.strafeRight,
    strafeMode,
  };
}

/**
 * Left-click — hit-test entities and issue a `setTarget` if we land on
 * one. Returns null if the click is on empty ground (no auto-clear).
 * Works in every phase (spectate UX during countdown).
 */
export function mapPointerToSetTarget(
  event: CanvasMouseEvent,
  ctx: MapperContext,
): MapResult {
  if (event.button !== 0) return NO_MESSAGE;
  if (ctx.snapshot === null) return NO_MESSAGE;
  const hit = hitTestEntity(event.point, ctx.snapshot, ctx.projectionCenter);
  if (hit === null) return NO_MESSAGE;
  let target: EntityId;
  try {
    target = _toEntityId(hit);
  } catch {
    return NO_MESSAGE;
  }
  return {
    kind: "intent",
    input: { kind: "setTarget", target },
  };
}

/**
 * Project the last known cursor position (#36) to a world aim point for a
 * skillshot cast. Returns `null` when the cursor is unknown or the projected
 * coords can't be branded (mirrors `mapPointerToMoveTo`'s clamp-negatives +
 * try/catch, via the shared `canvasPointToWorldUnits`). The sim re-clamps to
 * map bounds + range, so this only needs to produce a legal `Units` pair.
 */
function castAimFromPointer(
  ctx: MapperContext,
): { readonly x: Units; readonly y: Units } | null {
  if (ctx.lastPointer === null) return null;
  return canvasPointToWorldUnits(ctx.lastPointer, ctx.projectionCenter);
}

/**
 * Keyboard event → mapper. Uses `ctx.keymap` for the key mapping.
 * Auto-repeat (`event.repeat === true`) is passed through PURE-mapping-wise
 * for CAST — this function still resolves a repeat cast keydown to an
 * `intent` — but BLOCKED for cycle/clear/stop (those are edge-triggered —
 * see wp7 mapping table). #710: the CALLER (`InputSurface`'s `onKeyDown`)
 * additionally drops repeat `cast` intents before sending, the
 * same edge-triggered treatment as WASD — a held cast key must not spam a
 * `cast` send once per tick. That gate lives at the controller
 * (not here) because this function has no concept of "was the previous
 * keydown for this same key already sent" — it's a stateless per-event map.
 */
export function mapKeyEvent(event: KeyEvent, ctx: MapperContext): MapResult {
  if (ctx.snapshot === null) return NO_MESSAGE;

  // Normalize the event into a chord and resolve it to an action via the
  // reverse index. `chordId` folds modifiers in (#68), so Shift+1 ≠ 1 and
  // Ctrl/Cmd are unified cross-platform. Absent binding ⇒ inert key.
  const chord = normalizeChord(event.key, {
    ctrl: event.ctrl ?? false,
    alt: event.alt ?? false,
    shift: event.shift ?? false,
    meta: event.meta ?? false,
  });
  const action = buildReverseIndex(ctx.keymap).get(chordId(chord));
  if (action === undefined) return NO_MESSAGE;

  // Auto-repeat reaches THIS function for a cast (it is a stateless per-event
  // map and has no way to know whether the previous keydown for the same
  // physical key was already handled); every other action is edge-triggered and
  // swallows repeat noise right here. #710/#786: hold-to-re-cast does NOT ship
  // — the CALLER (`InputSurface`'s `onKeyDown`) drops repeat `cast` intents
  // before sending, exactly like WASD. See this function's doc comment.
  if (action.kind !== "castSlot" && event.repeat) return NO_MESSAGE;

  return actionToIntent(action, ctx);
}

/**
 * Translate a resolved `GameAction` into a `SimInput` intent. EXHAUSTIVE over
 * the action union — a new bindable action is a compile error until it has an
 * arm here. This is the single place action semantics live.
 */
export function actionToIntent(action: GameAction, ctx: MapperContext): MapResult {
  switch (action.kind) {
    case "castSlot": {
      // Cast requires the active phase. The slot is already a branded
      // AbilitySlot; empty slots are inert (the sim ignores them) until
      // content fills them.
      if (ctx.snapshot?.phase !== "active") return NO_MESSAGE;
      // #417/#530: `action.slot` is the VISUAL bar position the keybind fired
      // (see `resolveActionSlot`'s doc comment) — remap it to the real
      // ability slot the outgoing cast must carry. Identity when no resolver
      // is supplied (every existing call site/test, and every champion with
      // no saved layout). `null` means the visual position is EMPTY — no
      // ability to cast, so the keypress is a no-op rather than guessing a
      // fallback canonical slot (the #530 bug: a wrong fallback here could
      // cast a real but visually-unrelated ability).
      const slot = ctx.resolveActionSlot ? ctx.resolveActionSlot(action.slot) : action.slot;
      if (slot === null) return NO_MESSAGE;
      // #36: attach the cursor world-point as the aim. Skillshot abilities use
      // it; lock-on abilities ignore it. Always send when the cursor is known
      // so a ground ability never fizzles for lack of aim.
      const aim = castAimFromPointer(ctx);
      const cast: IntentPayload =
        aim === null ? { kind: "cast", slot } : { kind: "cast", slot, aim };
      // #815: auto-acquire. An `enemy`-targetType ability pressed with no
      // valid target used to be a SILENT no-op (the sim answers `noTarget`),
      // which reads as a broken keybind. Acquire the nearest enemy and send
      // the cast in the SAME press — one keystroke, one cast.
      const acquired = autoAcquireEnemyTarget(slot, ctx);
      if (acquired === null) return { kind: "intent", input: cast };
      return { kind: "intents", inputs: [{ kind: "setTarget", target: acquired }, cast] };
    }
    case "targetNearestEnemy": {
      // #815: Tab's new default. Acquires the NEAREST enemy when the hostile
      // slot is empty or stale; a no-op while the current target is still
      // alive and targetable (re-picking the same unit every press would make
      // the key feel dead — cycling lives in `cycleEnemyTarget`).
      if (ctx.snapshot === null || ctx.ownSeat === null) return NO_MESSAGE;
      return selectionToSetTarget(acquireNearestEnemy(ctx));
    }
    case "cycleEnemyTarget": {
      if (ctx.snapshot === null || ctx.ownSeat === null) return NO_MESSAGE;
      return selectionToSetTarget(nextEnemyTargetWire(ctx.snapshot, ctx.ownSeat));
    }
    case "cycleAllyTarget": {
      if (ctx.snapshot === null || ctx.ownSeat === null) return NO_MESSAGE;
      return selectionToSetTarget(nextAllyTargetWire(ctx.snapshot, ctx.ownSeat));
    }
    case "arenaTarget": {
      if (ctx.snapshot === null || ctx.ownSeat === null) return NO_MESSAGE;
      return selectionToSetTarget(
        enemyByIndexWire(ctx.snapshot, ctx.ownSeat, action.index),
      );
    }
    case "partyTarget": {
      if (ctx.snapshot === null || ctx.ownSeat === null) return NO_MESSAGE;
      return selectionToSetTarget(
        allyByIndexWire(ctx.snapshot, ctx.ownSeat, action.index),
      );
    }
    case "clearHostile":
      return { kind: "intent", input: { kind: "clearTarget", slot: "hostile" } };
    case "clearFriendly":
      return { kind: "intent", input: { kind: "clearTarget", slot: "friendly" } };
    case "stopCasting": {
      // #335: immediate cast/channel cancel. Same active-phase gate as every
      // other cast/move input; no target/aim needed. The sim's
      // `breakCastOrChannel` is a clean no-op when idle, so this is safe to
      // press with nothing in progress.
      if (ctx.snapshot?.phase !== "active") return NO_MESSAGE;
      return { kind: "intent", input: { kind: "stopCast" } };
    }
    case "toggleAutoAttack": {
      // #622: allowed pre-combat too (countdown + active — mirrors the sim's
      // `toggleAutoAttack` phase gate, same as setTarget/clearTarget), so a
      // player can flip the switch before the match goes active.
      return { kind: "intent", input: { kind: "toggleAutoAttack" } };
    }
    case "openChat": {
      // #833: CLIENT-LOCAL UI action — opening the chat box is not a sim
      // input and must never produce one. Deliberately NOT an omission: this
      // arm exists so `GameAction` stays exhaustively handled here, and so
      // that if chat ever DID need to reach the sim it would be a visible
      // edit to this line rather than a silent fall-through. `InputSurface`
      // intercepts this action before calling the mapper (it needs the store
      // dispatch the mapper is deliberately pure of); the mapper answering
      // "no message" is what makes that interception safe to forget — the
      // worst case is that Enter does nothing, never that it casts.
      return NO_MESSAGE;
    }
    default:
      return assertNeverAction(action);
  }
}

/**
 * #815: the SHARED nearest-enemy acquisition both entry points use — Tab
 * (`targetNearestEnemy`) and auto-acquire on an `enemy` cast. Returns the id
 * to select, or `null` for "nothing to do".
 *
 * `null` covers two distinct cases, deliberately collapsed because both mean
 * "send no setTarget": the current hostile target is STILL VALID (never steal
 * a deliberate target), or there is no living targetable enemy at all.
 *
 * NO RANGE OR FACING PRE-CHECK, on purpose: an out-of-range or behind-us
 * enemy is still selected, and the cast still goes out for the sim to reject
 * exactly as it does today (`cast.ts` range/facing gates). Duplicating those
 * gates here is the client/sim divergence this codebase keeps getting bitten
 * by; and a target visible in the unit frame is useful feedback even when the
 * cast fails — the next press then works.
 */
function acquireNearestEnemy(ctx: MapperContext): EntityId | null {
  if (ctx.snapshot === null || ctx.ownSeat === null) return null;
  if (hasLiveEnemyTargetWire(ctx.snapshot, ctx.ownSeat)) return null;
  return nearestEnemyTargetWire(ctx.snapshot, ctx.ownSeat);
}

/**
 * #815: the enemy to auto-acquire before casting `slot`, or `null` to cast
 * with the target exactly as it is.
 *
 * Gated on `targetType === "enemy"` — the ONE target type that both locks onto
 * an entity and is cheap to get wrong (auto-attacking the wrong enemy is
 * instantly visible; auto-healing the wrong ally is not, so `ally` is
 * deliberately excluded). `self`/`ground`/`none` have no entity target slot to
 * fill. An absent resolver (a pre-#815 context) means "unknown" ⇒ no acquire.
 */
function autoAcquireEnemyTarget(slot: AbilitySlot, ctx: MapperContext): EntityId | null {
  if (ctx.resolveTargetType === undefined) return null;
  if (ctx.resolveTargetType(slot) !== "enemy") return null;
  return acquireNearestEnemy(ctx);
}

/**
 * Turn a selection-helper result into a `setTarget` intent (or no-message on
 * `null`). A `null` id means "no valid selection" — an empty roster slot, a
 * corpse at a direct index, or no living target to cycle to — which is a
 * deliberate no-op (keep the current target), NOT a clear. The re-brand is
 * defensive: the id is already a wire `EntityId`, so the catch is unreachable
 * in practice but keeps this the single choke point for selection → intent.
 *
 * The target SLOT is decided by the sim's faction auto-routing on `setTarget`
 * (a same-team id lands in the friendly slot, an enemy id in the hostile slot),
 * so the selection helper only has to pick the right-faction entity — which it
 * does by construction (enemy helpers never return allies and vice versa).
 */
function selectionToSetTarget(id: EntityId | null): MapResult {
  if (id === null) return NO_MESSAGE;
  let target: EntityId;
  try {
    target = _toEntityId(id);
  } catch {
    return NO_MESSAGE;
  }
  return { kind: "intent", input: { kind: "setTarget", target } };
}

/** Exhaustiveness guard for the action switch. */
function assertNeverAction(x: never): never {
  throw new Error(`unhandled GameAction in actionToIntent: ${JSON.stringify(x)}`);
}

/** Convenience re-export for consumers that want the default keymap. */
export { DEFAULT_KEYMAP };
